import {AbstractControl, FormGroup} from '@angular/forms';
import {Observable} from 'rxjs';
import {NameValueCodeDTO} from 'src/app/modules/common/models/NameValueCodeDTO';
import {SharedService} from 'src/app/modules/common/services/shared-service';
import {DateUtil} from 'src/app/modules/common/util/DateUtil';
import {ApplicationPersonDTO} from 'src/app/modules/onboarding-admin-portal/models/application/ApplicationPersonDTO';
import {ApplicationDTO} from '../../../onboarding-admin-portal/models/application/ApplicationDTO';
import {ApplicationStatusLookupDTO} from '../../../onboarding-admin-portal/models/application/ApplicationStatusLookupDTO';
import {ApplicationStatusResponseDTO} from '../../../onboarding-admin-portal/models/application/ApplicationStatusResponseDTO';
import {ReferenceDTO} from '../../../onboarding-admin-portal/models/application/ReferenceDTO';
import {StartDateOption} from '../components/obd-bulk-application-import/obd-bulk-application-import.component';
import {ApplicationPersonVerificationResult} from '../services/application-person-verification.service';

export class ApplicationUtil {
  static readonly PRINCIPAL_MEMBER_ASSOCIATION_CODE = '1';
  static readonly EMPLOYEE_NUMBER_FIELD_NAME = 'employeeNumber';
  static readonly EMPLOYMENT_DATE_FIELD_NAME = 'employmentDate';
  static readonly SID_ID_TYPE = 'SID';
  static readonly PASSED_ID_CHECK = 'passedIdCheck';
  static readonly ID_TYPE = 'idType';
  static readonly NATIONAL_ID_NUMBER = 'nationalIdNumber';
  static readonly PASSPORT_NUMBER = 'passportNumber';

  static mapReferenceToNameValue(references: ReferenceDTO[]): any {
    if (!references || references.length === 0) {
      return [];
    }

    return references
      .sort((a, b) => a.value.localeCompare(b.value))
      .map((ref: ReferenceDTO) => ({
        name: ref.value,
        value: ref.key
      }));
  }

  static getPrincipalFromApplication(application: ApplicationDTO) {
    return application?.membership?.persons?.find(
      person => person.associationCode === ApplicationUtil.PRINCIPAL_MEMBER_ASSOCIATION_CODE
    );
  }

  static associationCodeToRelationshipTypeCodeMapping(relationshipTypeCode: string): string {
    const associationMap: Record<string, string> = {
      SELF: '1',
      CLSPO: '2',
      DSPO: '2',
      SPO: '2',
      CHI: '3',
      FCHI: '3',
      GCHI: '4',
      OCHI: '4',
      OTHER: '4',
      PAR: '4',
      SIB: '4',
      STU: '5',
      ACHI: '7',
      DCHI: '8'
    };

    return associationMap[relationshipTypeCode] || '4'; // Default to 4 if not found.
  }

  static getApplicationReferenceNumber(application: ApplicationDTO): string {
    const schemeCode = application?.scheme?.schemeCode?.trim() || '';
    const optionCode = application?.membership?.optionCode || '';
    const joinDate = application?.membership?.startDate ? new Date(application.membership.startDate).toISOString().slice(0, 10) : '';
    const intermediaryCode = application?.intermediatory?.intermediatoryCode || '';
    const mainApplicant = this.getPrincipalMember(application);
    const idNumber = mainApplicant?.identification?.idNumber || '';
    return `${schemeCode}-${optionCode}-${joinDate}-${intermediaryCode}-${idNumber}`;
  }


  static getPrincipalMember(application: ApplicationDTO): ApplicationPersonDTO {
    if (!application || !application.membership || !application.membership.persons) {
      return null;
    }
    return application.membership?.persons.find(person => person.associationCode === this.PRINCIPAL_MEMBER_ASSOCIATION_CODE);
  }

  static isInEmployerGroupList(employerGroupList: ReferenceDTO[], employerGroupCode: string): boolean {
    if (employerGroupList == null || employerGroupList.length == 0) {
      return false;
    }
    if (employerGroupCode == null || employerGroupCode.trim() === '') {
      return false;
    }
    return employerGroupList.some(group => group.key === employerGroupCode);
  }

  static getCodeFromString(input: string | null | undefined): string | null {
    if (!input || input.trim() === '') {
      return null;
    }

    input = input.trim();
    const inputParts = input.split(':');
    if (inputParts.length === 0) {
      return null;
    }

    const code = inputParts[0];
    if (!code || code.trim() === '') {
      return null;
    }

    return code.replace(/\s+/g, '').toUpperCase();
  }

  static shouldShowGroupFields(employerGroupList: ReferenceDTO[], employerGroupCode: string, isPrincipal: boolean): boolean {
    // non-direct principal members (i.e. employer group principal members) require employee number and date fields
    const isDirectPayingMember = ApplicationUtil.isInEmployerGroupList(employerGroupList, employerGroupCode);
    return isPrincipal && !isDirectPayingMember;
  }

  static setEmployeeGroupFieldsEnabledFlag(personalDetailsFormGroup: FormGroup, employerGroupList: ReferenceDTO[], employerGroupCode: string, isPrincipal: boolean) {
    if (this.shouldShowGroupFields(employerGroupList, employerGroupCode, isPrincipal)) {
      personalDetailsFormGroup?.get(ApplicationUtil.EMPLOYEE_NUMBER_FIELD_NAME)?.enable();
      personalDetailsFormGroup?.get(ApplicationUtil.EMPLOYMENT_DATE_FIELD_NAME)?.enable();
    }
    else {
      personalDetailsFormGroup?.get(ApplicationUtil.EMPLOYEE_NUMBER_FIELD_NAME)?.disable();
      personalDetailsFormGroup?.get(ApplicationUtil.EMPLOYMENT_DATE_FIELD_NAME)?.disable();
    }
  }

  static isFreeTextCondition(conditionCode: string, diagnosisGroups: NameValueCodeDTO[]): boolean {
    if (!conditionCode || conditionCode.trim() === '') {
      return false;
    }

    const existsInList = diagnosisGroups?.some(
      group => group.code === conditionCode
    );

    return !existsInList;
  }

  static convertBooleanToInteger(value: boolean): number {
    return value ? 1 : 0;
  }

  static isSIDTypeSelected(idType: string): boolean {
    return idType === this.SID_ID_TYPE;
  }

  static isPassportTypeSelected(idType: string): boolean {
    return !this.isSIDTypeSelected(idType);
  }

  static getIdType(formGroup: FormGroup) {
    return formGroup?.get(this.ID_TYPE)?.value;
  }

  static getPassedIdCheckValue(formGroup: FormGroup): boolean {
    return formGroup?.get(this.PASSED_ID_CHECK)?.value;
  }

  static setPassedIdCheckValue(formGroup: FormGroup, value: boolean): void {
    formGroup.patchValue({
      [this.PASSED_ID_CHECK]: value,
    });
  }

  static getActiveIdentificationControl(formGroup: FormGroup): AbstractControl | null {
    return ApplicationUtil.isSIDTypeSelected(this.getIdType(formGroup))
      ? formGroup?.get(this.NATIONAL_ID_NUMBER)
      : formGroup?.get(this.PASSPORT_NUMBER);
  }

  static getActiveIdentificationValue(formGroup: FormGroup): string {
    return this.getActiveIdentificationControl(formGroup)?.value;
  }

  static isActiveIdentificationValid(formGroup: FormGroup): boolean {
    return this.getActiveIdentificationControl(formGroup)?.valid ?? false;
  }

  static handleIdVerificationError(verificationResult: ApplicationPersonVerificationResult, errorMessage: string, formGroup: FormGroup): void {
    verificationResult.errorVerificationMessage = errorMessage;
    verificationResult.errorVerificationIndicator = -1;
    this.resetApplicationIdVerification(verificationResult, formGroup);
  }

  static resetApplicationIdVerification(verificationResult: ApplicationPersonVerificationResult, formGroup: FormGroup): void {
    verificationResult.applicationVerificationMessage = null;
    verificationResult.applicationVerificationIndicator = 0;
    ApplicationUtil.setPassedIdCheckValue(formGroup, false);
  }

  static getIsAliveIsVerified(verificationResult: ApplicationPersonVerificationResult, applicationPerson: ApplicationPersonDTO) {
    let isAlive = applicationPerson?.identification?.isAlive || 0; // Use existing value, otherwise default to 0
    let isVerified = applicationPerson?.identification?.isVerified || 0; // Use existing value, otherwise default to 0
    // If there is a successful government response, set the isAlive and isVerified
    if (verificationResult.governmentResponseComplete && verificationResult.governmentVerificationIndicator > -1) {
      // If the ID has changed or the ID is valid, use the government response indicators
      if (verificationResult.idChanged || verificationResult.governmentVerificationIndicator == 1) {
        isAlive = ApplicationUtil.convertBooleanToInteger(!verificationResult.governmentDeadIndicator);
        isVerified = ApplicationUtil.convertBooleanToInteger(verificationResult.governmentIdMatchIndicator);
      }
    }
    return {isAlive, isVerified};
  }

  static generateStartDateOptions(): StartDateOption[] {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    // Generate options: 1 month back (-1), current month (0), 1 month forward (1) etc.
    const monthOffsets = [-1, 0, 1, 2, 3];

    return monthOffsets.map(offset => {
      const date = new Date(currentYear, currentMonth + offset, 1);
      return {
        value: this.formatDate(date),
        displayValue: this.formatDate(date),
        isCurrentMonth: offset === 0
      };
    });
  }

  static formatDate(date: Date): string {
    if (date) {
      return SharedService.formatDate(date, DateUtil.dateFormat);
    }
    else {
      return '';
    }
  }

  static extractStatusFromApplication(application: ApplicationDTO): Observable<ApplicationStatusLookupDTO> {
    if (application != null) {
      const status = new ApplicationStatusLookupDTO();
      status.response = new ApplicationStatusResponseDTO();
      const filteredRuleMessagesByStatus = application.ruleMessages
        .filter(app => application.status.statusName.includes(app.actionCode))
        .filter(app => app.isCurrentIndicator)
        .sort((rm1, rm2) => rm2.applicationRuleMessageId - rm1.applicationRuleMessageId);

      status.response.status = application.status.statusName;
      status.response.statusDescription = filteredRuleMessagesByStatus.length > 0 ? filteredRuleMessagesByStatus[0].reasonDescription : this.getDefaultedStatusMessage(application.status.statusName);
      status.response.messages = filteredRuleMessagesByStatus.map(rm => rm.reasonDescription);

      return new Observable(observer => {
        observer.next(status);
        observer.complete();
      });
    }
  }

  static getDefaultedStatusMessage(statusName: string): string {
    switch (statusName) {
      case 'Pended': return 'The application has been pended and is awaiting further processing.';
      case 'Accepted': return 'The application has been accepted.';
      case 'Rejected': return 'The application has been rejected.';
      default: return 'The application has not yet been processed.';
    }
  }

}
